/**
 * Single source of truth for all subject-level display data.
 *
 * Canonical subject names come from topics_data.json (497 topics).
 * Any component needing subject colours, border classes, dot colours, or
 * abbreviations must import from here — never define its own map.
 *
 * CSS variables and border classes are defined in design-system.css.
 */

// ─── Hex colours (used directly where CSS vars are not available) ─────────────

export const SUBJECT_COLOURS = {
  'Business Law and Practice':              '#3B82F6',
  'Property Law and Practice':             '#22C55E',
  'Contract Law':                           '#F59E0B',
  'Tort':                                   '#EF4444',
  'Dispute Resolution':                     '#8B5CF6',
  'Criminal Law and Practice':             '#EC4899',
  'Criminal Liability':                     '#EC4899',
  'Wills and the Administration of Estates': '#14B8A6',
  'Solicitors Accounts':                   '#6B7280',
  'Land Law':                              '#059669',
  'Trusts Law':                            '#7C3AED',
  'Legal Services':                        '#F97316',
  'Legal System':                          '#64748B',
  'Constitutional and Administrative Law': '#0EA5E9',
};

// ─── CSS border classes (defined in design-system.css .b-* rules) ────────────

export const SUBJECT_BORDER_CLASS = {
  'Business Law and Practice':              'b-blp',
  'Property Law and Practice':             'b-property',
  'Contract Law':                           'b-contract',
  'Tort':                                   'b-tort',
  'Dispute Resolution':                     'b-dispute',
  'Criminal Law and Practice':             'b-criminal',
  'Criminal Liability':                     'b-criminal',
  'Wills and the Administration of Estates': 'b-wills',
  'Solicitors Accounts':                   'b-accounts',
  'Land Law':                              'b-land',
  'Trusts Law':                            'b-trusts',
  'Legal Services':                        'b-legal-services',
  'Legal System':                          'b-legal-system',
  'Constitutional and Administrative Law': 'b-const',
};

// ─── CSS variable dot colours (for inline style use) ─────────────────────────

export const SUBJECT_DOT_COLOUR = {
  'Business Law and Practice':              'var(--subj-blp)',
  'Property Law and Practice':             'var(--subj-property)',
  'Contract Law':                           'var(--subj-contract)',
  'Tort':                                   'var(--subj-tort)',
  'Dispute Resolution':                     'var(--subj-dispute)',
  'Criminal Law and Practice':             'var(--subj-criminal)',
  'Criminal Liability':                     'var(--subj-criminal)',
  'Wills and the Administration of Estates': 'var(--subj-wills)',
  'Solicitors Accounts':                   'var(--subj-accounts)',
  'Land Law':                              'var(--subj-land)',
  'Trusts Law':                            'var(--subj-trusts)',
  'Legal Services':                        'var(--subj-legal-services)',
  'Legal System':                          'var(--subj-legal-system)',
  'Constitutional and Administrative Law': 'var(--subj-const)',
};

// ─── Short display names for space-constrained UI ────────────────────────────

export const SUBJECT_ABBREVS = {
  'Business Law and Practice':              'BLP',
  'Property Law and Practice':             'Property',
  'Contract Law':                           'Contract',
  'Tort':                                   'Tort',
  'Dispute Resolution':                     'Dispute',
  'Criminal Law and Practice':             'Criminal',
  'Criminal Liability':                     'Criminal Liab.',
  'Wills and the Administration of Estates': 'Wills',
  'Solicitors Accounts':                   'Accounts',
  'Land Law':                              'Land Law',
  'Trusts Law':                            'Trusts',
  'Legal Services':                        'Legal Serv.',
  'Legal System':                          'Legal Sys.',
  'Constitutional and Administrative Law': 'Const. & Admin.',
};

// ─── Helper functions (all use exact match then partial match then fallback) ──

function _lookup(map, sa, fallback) {
  if (!sa) return fallback;
  if (map[sa]) return map[sa];
  const lower = sa.toLowerCase();
  for (const [k, v] of Object.entries(map)) {
    if (lower.includes(k.toLowerCase())) return v;
  }
  return fallback;
}

/** Hex colour for a subject area string. */
export function subjectColour(sa) {
  return _lookup(SUBJECT_COLOURS, sa, '#CBD5E1');
}

/** CSS border class for a subject area string (e.g. "b-blp"). */
export function subjectBorderClass(sa) {
  return _lookup(SUBJECT_BORDER_CLASS, sa, '');
}

/** CSS variable dot colour for a subject area string (e.g. "var(--subj-blp)"). */
export function subjectDotColour(sa) {
  return _lookup(SUBJECT_DOT_COLOUR, sa, 'var(--text-tertiary)');
}

/** Short abbreviation for a subject area string. */
export function subjectAbbrev(sa) {
  return _lookup(SUBJECT_ABBREVS, sa, sa || '');
}
